import { z } from "zod";
import {
  type Fonts,
  type MonospaceFonts,
  FONT_KEYS,
  MONOSPACE_FONT_KEYS,
} from "./fonts";

const hex = z.string().regex(/^#[0-9a-f]{6}$/i);
export const themeSnapshotSchema = z
  .object({
    mode: z.enum(["dark", "light", "system"]),
    m3Accent: hex,
    iconAccent: hex,
    iconStyle: z.enum(["outline", "filled", "expressive"]),
    iconSize: z.number().int().min(12).max(32),
    iconFill: z.number().min(0).max(1),
    radius: z.number().min(0).max(24),
    railWidth: z.number().min(48).max(100),
    sidebarWidth: z.number().min(180).max(360),
    density: z.enum(["compact", "comfortable", "spacious"]),
    blur: z.boolean(),
    interfaceFont: z.custom<Fonts>(
      (value) =>
        typeof value === "string" && (FONT_KEYS as string[]).includes(value),
    ),
    monospaceFont: z.custom<MonospaceFonts>(
      (value) =>
        typeof value === "string" &&
        (MONOSPACE_FONT_KEYS as string[]).includes(value),
    ),
    messageSize: z.number().min(12).max(24),
    messageGroupSpacing: z.number().min(0).max(64),
    surfaces: z
      .object({
        bgApp: hex.optional(),
        bgRail: hex.optional(),
        bgSidebar: hex.optional(),
        bgChat: hex.optional(),
        bgRaised: hex.optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export const savedThemeSchema = z
  .object({
    version: z.literal(1),
    name: z.string().trim().min(1).max(60),
    settings: themeSnapshotSchema,
  })
  .strict();
export type ThemeSnapshot = z.infer<typeof themeSnapshotSchema>;
export type SavedTheme = z.infer<typeof savedThemeSchema>;
export const SAVED_THEME_KEY = "zarzour.saved-themes.v1";

export function parseThemeImport(source: string): SavedTheme {
  if (source.length > 65536)
    throw new Error("Theme files must be smaller than 64 KB.");
  return savedThemeSchema.parse(JSON.parse(source));
}

export function readSavedThemes(): SavedTheme[] {
  return z
    .array(savedThemeSchema)
    .max(32)
    .parse(JSON.parse(localStorage.getItem(SAVED_THEME_KEY) ?? "[]"));
}

export function writeSavedThemes(themes: SavedTheme[]) {
  const validated = z.array(savedThemeSchema).max(32).parse(themes);
  localStorage.setItem(SAVED_THEME_KEY, JSON.stringify(validated));
}
